/**
 * Short how-to copy for admin list/tool pages.
 */
export const ADMIN_PAGE_GUIDES = {
  dashboard: {
    title: 'Using the dashboard',
    steps: [
      'Scan key metrics for bookings, revenue, and vendors.',
      'Change the period to compare weeks, months, or quarters.',
      'Jump to Bookings, Vendors, or Payments from quick actions when you need detail.',
    ],
  },
  clients: {
    title: 'Managing clients',
    steps: [
      'Search or filter to find a client account.',
      'Open a profile to review bookings, payments, and status.',
      'Use export when you need a client list for support or finance.',
    ],
  },
  clientDetail: {
    title: 'This client',
    steps: [
      'Review account details and recent activity.',
      'Check bookings and payments linked to this client.',
      'Update status only when support or compliance requires it.',
    ],
  },
  vendors: {
    title: 'Managing vendors',
    steps: [
      'Filter by verification or subscription status when reviewing the marketplace.',
      'Open a vendor to approve verification, check services, or review payouts.',
      'Keep high-quality vendors active — suspend only with a clear reason.',
    ],
  },
  vendorDetail: {
    title: 'This vendor',
    steps: [
      'Confirm verification documents and business profile.',
      'Review services, bookings, invoices, and contracts from the hub cards.',
      'Record payout or compliance actions from Finance when needed.',
    ],
  },
  vendorInvoices: {
    title: 'Vendor invoices',
    steps: [
      'Browse invoices this vendor created for clients.',
      'Open an invoice to review line items, status, and totals.',
      'Use search or status filters when reconciling a dispute.',
    ],
  },
  vendorInvoiceDetail: {
    title: 'Invoice detail',
    steps: [
      'Confirm client, amounts, and payment status.',
      'Download a PDF copy for support or finance records.',
      'Cross-check the linked booking when amounts look off.',
    ],
  },
  vendorContracts: {
    title: 'Vendor contracts',
    steps: [
      'See contracts this vendor sent for client signature.',
      'Open a contract to read the body and signing status.',
      'Draft/sent/signed statuses help track completion.',
    ],
  },
  vendorContractDetail: {
    title: 'Contract detail',
    steps: [
      'Read the contract body and party details.',
      'Check signed / viewed timestamps when resolving disputes.',
      'Return to the vendor profile for services and bookings context.',
    ],
  },
  bookings: {
    title: 'Platform & private bookings',
    steps: [
      'Switch between platform and private (vendor-logged) bookings.',
      'Filter by status or date, then open a booking for full detail.',
      'Export when finance or ops needs a report.',
    ],
  },
  bookingDetail: {
    title: 'Booking detail',
    steps: [
      'Confirm client, vendor, schedule, and payment state.',
      'Use status tools carefully — clients and vendors see updates.',
      'Cross-check payments or refunds from the Finance section if needed.',
    ],
  },
  calendar: {
    title: 'Bookings calendar',
    steps: [
      'See marketplace events by month at a glance.',
      'Click a day or event to open the booking.',
      'Use list view under Bookings when you need filters and export.',
    ],
  },
  payments: {
    title: 'Marketplace payments',
    steps: [
      'Review client payments tied to bookings.',
      'Filter by status or date to reconcile issues.',
      'Pair with Refunds and Accounting for a full money trail.',
    ],
  },
  refunds: {
    title: 'Refunds',
    steps: [
      'Track refund requests and completed refunds.',
      'Confirm booking and payment before approving.',
      'Document reasons so support and finance stay aligned.',
    ],
  },
  subscriptions: {
    title: 'Vendor SaaS plans',
    steps: [
      'Create and edit plans vendors buy — pricing, limits, and page access.',
      'Map Stripe product/price IDs so paid plans can charge monthly or yearly.',
      'Use Subscription Payments for invoice-level charge history.',
    ],
  },
  subscriptionPayments: {
    title: 'Read plan charges like a ledger',
    steps: [
      'These are vendor SaaS plan payments to the platform — not client booking payments.',
      'Filter by status, plan, or date range to find completed, pending, failed, or refunded charges.',
      'Open a vendor or Plans when you need to follow up on a failed or mismatched charge.',
    ],
  },
  vendorPayouts: {
    title: 'Settle vendor accounts',
    steps: [
      'Only completed events create payable balance — paid but unfinished events stay held.',
      'Record each external payout so the ledger stays accurate.',
      'Open Accounting for the full credit/debit statement per vendor.',
    ],
  },
  accounting: {
    title: 'Platform accounting',
    steps: [
      'Scan platform income (marketplace fees + subscriptions), vendor liability, and refunds.',
      'Use the period snapshot for month/quarter revenue, then drill into Payments or Revenue.',
      'Search a vendor to open their ledger — record payouts from Vendor Payouts when you settle.',
    ],
  },
  analytics: {
    title: 'Analytics',
    steps: [
      'Pick a period to compare growth.',
      'Use charts for bookings and revenue trends.',
      'Open Advanced Analytics for deeper slices.',
    ],
  },
  advancedAnalytics: {
    title: 'Advanced analytics',
    steps: [
      'Explore detailed breakdowns beyond the main dashboard.',
      'Compare cohorts, conversion, or category performance.',
      'Export or screenshot when briefing stakeholders.',
    ],
  },
  reports: {
    title: 'Reports',
    steps: [
      'Choose the report type your team needs.',
      'Apply date ranges before download.',
      'Use Services report for catalog health separately.',
    ],
  },
  servicesReport: {
    title: 'Services report',
    steps: [
      'See how services perform across vendors.',
      'Spot underused or high-demand categories.',
      'Follow up in Vendor Services or Categories as needed.',
    ],
  },
  revenue: {
    title: 'Revenue',
    steps: [
      'Monitor platform take and booking revenue over time.',
      'Exclude private bookings from platform revenue views.',
      'Cross-check Payments and Payouts for reconciliation.',
    ],
  },
  performance: {
    title: 'Performance',
    steps: [
      'Track operational health of the marketplace.',
      'Watch completion, cancellations, and response trends.',
      'Escalate outliers to Support or Vendor Management.',
    ],
  },
  failedOperations: {
    title: 'Failed operations',
    steps: [
      'Triage payment, email, or job failures here.',
      'Retry or resolve with a clear owner.',
      'Escalate repeated failures to engineering.',
    ],
  },
  content: {
    title: 'Site content',
    steps: [
      'Update marketing or CMS blocks that power public pages.',
      'Preview before publishing when available.',
      'Keep copy consistent with Brand and Blog posts.',
    ],
  },
  blogPosts: {
    title: 'Blog posts',
    steps: [
      'Draft or publish posts for the public blog.',
      'Use clear titles and featured images.',
      'Unpublish outdated posts instead of leaving stale content live.',
    ],
  },
  vendorSpotlight: {
    title: 'Vendor spotlight',
    steps: [
      'Feature strong vendors on public surfaces.',
      'Rotate spotlights so more vendors get visibility.',
      'Keep descriptions accurate and approved.',
    ],
  },
  vendorSuccessStories: {
    title: 'Success stories',
    steps: [
      'Publish real vendor wins with permission.',
      'Include outcomes clients care about.',
      'Archive stories that no longer reflect the brand.',
    ],
  },
  messages: {
    title: 'Admin messages',
    steps: [
      'Review conversation threads that need admin attention.',
      'Reply promptly and keep a professional tone.',
      'Escalate disputes to Support Tickets when needed.',
    ],
  },
  contactMessages: {
    title: 'Contact form inbox',
    steps: [
      'Triage inbound messages from the public contact form.',
      'Mark resolved after you reply or route them.',
      'Watch for spam and phishing before clicking links.',
    ],
  },
  notifications: {
    title: 'Notifications',
    steps: [
      'See system and admin alerts in one place.',
      'Mark read after you act.',
      'Use Announcements for platform-wide broadcasts.',
    ],
  },
  systemMessages: {
    title: 'System messages',
    steps: [
      'Review automated platform notices.',
      'Investigate repeated errors with Failed Operations.',
      'Keep critical alerts visible until resolved.',
    ],
  },
  announcements: {
    title: 'Announcements',
    steps: [
      'Create banners or notices vendors and clients will see.',
      'Set clear start/end times when available.',
      'Keep wording short — link out for longer docs.',
    ],
  },
  supportTickets: {
    title: 'Support tickets',
    steps: [
      'Prioritize unread and urgent tickets first.',
      'Reply with next steps and ownership.',
      'Close only when the requester confirms or the issue is fully resolved.',
    ],
  },
  reviews: {
    title: 'Moderating reviews',
    steps: [
      'Pending reviews stay hidden from public vendor profiles until you publish.',
      'Hide published reviews that are abusive or off-policy without deleting history.',
      'Delete only when content must be removed permanently.',
    ],
  },
  supportTicketDetail: {
    title: 'This ticket',
    steps: [
      'Read the full thread before replying.',
      'Update status as you work (in progress → resolved).',
      'Link related bookings or vendors in your reply when helpful.',
    ],
  },
  faqs: {
    title: 'FAQ management',
    steps: [
      'Keep answers current with product changes.',
      'Group related questions clearly.',
      'Retire outdated FAQs instead of leaving wrong info live.',
    ],
  },
  services: {
    title: 'Vendor services',
    steps: [
      'Browse services listed across the marketplace.',
      'Flag or moderate listings that break policy.',
      'Use Categories and Countries to keep taxonomy clean.',
    ],
  },
  serviceCategories: {
    title: 'Service categories',
    steps: [
      'Maintain the category tree clients browse.',
      'Avoid duplicate or overlapping category names.',
      'Reorder or rename carefully — vendors inherit these labels.',
    ],
  },
  countries: {
    title: 'Countries',
    steps: [
      'This is the reference catalog: names, codes, and flags.',
      'It does not decide where the marketplace operates.',
      'Add a market to use a country for vendors, checkout, and tax.',
    ],
  },
  markets: {
    title: 'Markets',
    steps: [
      'A market is a country where the marketplace operates.',
      'Vendor verification, checkout, and tax lists only these markets.',
      'Keep a market in Draft until it is ready, then set it to Active.',
      'Pause a market anytime without redeploying the app.',
    ],
  },
  settings: {
    title: 'Platform settings',
    steps: [
      'Update your admin Account, Password, and Two-Factor Auth first.',
      'Platform tabs (commission, email, site) affect all users — change carefully.',
      'Save section by section and document major platform changes.',
    ],
  },
  security: {
    title: 'Security',
    steps: [
      'Review admin access and sensitive controls.',
      'Rotate credentials and enforce strong practices.',
      'Pair with System Logs when investigating incidents.',
    ],
  },
  systemLogs: {
    title: 'System logs',
    steps: [
      'Filter by time and severity during incidents.',
      'Capture IDs and timestamps before escalating.',
      'Do not share raw logs publicly.',
    ],
  },
  termsOfService: {
    title: 'Terms of Service',
    steps: [
      'Edit legal copy with counsel when possible.',
      'Publish only after a careful review.',
      'Note the effective date for users.',
    ],
  },
  privacyPolicy: {
    title: 'Privacy Policy',
    steps: [
      'Keep privacy disclosures aligned with real data practices.',
      'Update when you add processors or tracking.',
      'Publish clearly so users can find the latest version.',
    ],
  },
  complianceAudit: {
    title: 'Compliance audit',
    steps: [
      'Run or review audit checks regularly.',
      'Fix gaps with a named owner and due date.',
      'Keep evidence for regulators or partners when asked.',
    ],
  },
  pressReleases: {
    title: 'Press releases',
    steps: [
      'Draft company news for the public press page.',
      'Set a release date before publishing.',
      'Unpublish outdated releases instead of leaving stale news live.',
    ],
  },
  jobPostings: {
    title: 'Job postings',
    steps: [
      'Create openings with clear role titles and locations.',
      'Close roles that are filled.',
      'Keep descriptions accurate for applicants.',
    ],
  },
  emailTemplates: {
    title: 'Email templates',
    steps: [
      'Edit templates carefully — they send to real users.',
      'Preview before saving when available.',
      'Keep tone consistent with brand.',
    ],
  },
  auditTrail: {
    title: 'Audit trail',
    steps: [
      'Filter by actor or time when investigating changes.',
      'Capture IDs before escalating.',
      'Do not share raw audit exports publicly.',
    ],
  },
  compliance: {
    title: 'Compliance records',
    steps: [
      'Track compliance documents and status.',
      'Update when policies or vendors change.',
      'Pair with Compliance Audit for checks.',
    ],
  },
  termsPrivacy: {
    title: 'Terms & privacy docs',
    steps: [
      'Manage legal document versions here.',
      'Prefer dedicated Terms / Privacy pages for primary copy.',
      'Publish only after review.',
    ],
  },
  dataProtection: {
    title: 'Data protection',
    steps: [
      'Review data-protection requests and records.',
      'Act within legal timelines.',
      'Coordinate with Security and Logs when needed.',
    ],
  },
  supportPreview: {
    title: 'Vendor support preview',
    steps: [
      'This page previews what vendors see.',
      'Edit content under Settings → Vendor Support.',
      'Refresh after saving settings to verify copy.',
    ],
  },
};
